import './SingleDocumentSectionCard.scss';
import { ReactElement, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MdAdd,
  MdAutoAwesome,
  MdOpenInNew,
  MdRefresh,
  MdSyncAlt,
  MdUploadFile
} from 'react-icons/md';
import { RiLinkM } from 'react-icons/ri';
import {
  OnboardingSectionVersion,
  OnboardingStoreType,
  SummarySource
} from '@blisspointmedia/bpm-types/dist/ClientOracleOnboarding';
import { Button } from '@Components/Button';
import { Appearance, Size } from '@Components/designConstants';
import { EmptyState } from '@Components/EmptyState';
import { Spinner } from '@Components/Spinner/Spinner';
import { dateToLongDate } from '@src/utils/date-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { fetchSourceDownloadUrl } from '../onboarding-api';
import { OnboardingCardKind, OnboardingCardState, versionIdsOf } from '../useOnboardingSections';
import { useOpenDocument } from '../useOpenDocument';
import { KebabMenu } from './KebabMenu';
import { SectionCard } from './SectionCard';

interface SingleDocumentSectionCardProps {
  icon: ReactElement;
  label: string;
  emptyStateCopy: string;
  addButtonLabel: string;
  state: OnboardingCardState;
  isRetrying?: boolean;
  onAddSource: () => void;
  onRetrySummary: (versionId: number, store: OnboardingStoreType) => void;
}

// A section whose body is an uploaded document and the AI summary derived from it.
export const SingleDocumentSectionCard = ({
  icon,
  label,
  emptyStateCopy,
  addButtonLabel,
  state,
  isRetrying = false,
  onAddSource,
  onRetrySummary
}: SingleDocumentSectionCardProps) => {
  const { cid } = useCompanyInfo();
  const openDocument = useOpenDocument();

  // The signed URL is minted on click, not at render, where it could expire before anyone clicks.
  const handleOpenSource = useCallback(
    (store: OnboardingStoreType, versionId: number) =>
      openDocument(() => fetchSourceDownloadUrl({ company: cid, store, versionId })),
    [cid, openDocument]
  );

  const versionIds = versionIdsOf(state);
  const retryableVersionId = versionIds?.retryableVersionId;

  return (
    <SectionCard
      icon={icon}
      label={label}
      className='single-document-section-card'
      rightActions={
        <SingleDocumentActionsMenu
          onUpload={onAddSource}
          onOpen={
            versionIds
              ? () => handleOpenSource(versionIds.versionStore, versionIds.documentVersionId)
              : undefined
          }
          onRetry={
            versionIds && retryableVersionId !== undefined
              ? () => onRetrySummary(retryableVersionId, versionIds.versionStore)
              : undefined
          }
          isRetrying={isRetrying}
        />
      }
    >
      {state.kind === OnboardingCardKind.EMPTY ? (
        <EmptyState className='onboarding-section-card-empty-state' description={emptyStateCopy}>
          <Button size={Size.LG} leadingElement={<MdAdd />} onClick={onAddSource}>
            {addButtonLabel}
          </Button>
        </EmptyState>
      ) : (
        <SingleDocumentSectionBody state={state} onOpenSource={handleOpenSource} />
      )}
    </SectionCard>
  );
};

interface ErrorStatusProps {
  message: string;
}

const ErrorStatus = ({ message }: ErrorStatusProps) => (
  <div className='section-status error'>{message}</div>
);

const SectionAction = {
  UPLOAD: 'upload',
  OPEN: 'open',
  RETRY: 'retry'
} as const;
type SectionActionType = (typeof SectionAction)[keyof typeof SectionAction];

interface SingleDocumentActionsMenuProps {
  // An upload inserts a new version whether or not one exists already.
  onUpload: () => void;
  // Present only when the section has a document: it gates Open, and makes the upload item read
  // as Replace rather than Add.
  onOpen?: () => void;
  // Present only when a summary failed. Re-runs the summary against the existing document.
  onRetry?: () => void;
  // A retry is already in flight for this section, so the item is offered but refused.
  isRetrying?: boolean;
}

const retryTooltip = (canRetry: boolean, isRetrying: boolean): string | undefined => {
  if (isRetrying) return 'A retry is already running';
  return canRetry ? undefined : 'Only available after a summary fails';
};

const SingleDocumentActionsMenu = ({
  onUpload,
  onOpen,
  onRetry,
  isRetrying = false
}: SingleDocumentActionsMenuProps) => {
  const hasDocument = Boolean(onOpen);
  const canRetry = Boolean(onRetry) && !isRetrying;

  return (
    <KebabMenu<SectionActionType>
      items={[
        {
          value: SectionAction.UPLOAD,
          label: hasDocument ? 'Replace document' : 'Add document via upload',
          leadingContent: hasDocument ? <MdSyncAlt /> : <MdUploadFile />
        },
        {
          value: SectionAction.OPEN,
          label: 'Open',
          leadingContent: <MdOpenInNew />,
          disabled: !hasDocument,
          simpleTooltip: hasDocument ? undefined : 'No document uploaded yet'
        },
        {
          value: SectionAction.RETRY,
          label: isRetrying ? 'Retrying AI summary...' : 'Retry AI summary',
          leadingContent: <MdRefresh />,
          disabled: !canRetry,
          simpleTooltip: retryTooltip(canRetry, isRetrying)
        }
      ]}
      onSelect={value => {
        if (value === SectionAction.OPEN) {
          onOpen?.();
          return;
        }
        if (value === SectionAction.RETRY) {
          onRetry?.();
          return;
        }
        onUpload();
      }}
    />
  );
};

interface SingleDocumentSectionBodyProps {
  // state.kind is never 'empty' here -- SingleDocumentSectionCard renders EmptyState for that case.
  state: Exclude<OnboardingCardState, { kind: typeof OnboardingCardKind.EMPTY }>;
  onOpenSource: (store: OnboardingStoreType, versionId: number) => void;
}

const SingleDocumentSectionBody = ({ state, onOpenSource }: SingleDocumentSectionBodyProps) => {
  if (state.kind === OnboardingCardKind.SUMMARIZING) {
    return (
      <div className='section-status'>
        <Spinner size={20} />
        <span>Summarizing…</span>
      </div>
    );
  }
  if (state.kind === OnboardingCardKind.FAILED) {
    return <ErrorStatus message={state.message} />;
  }
  return (
    <>
      <SourceBar
        version={state.version}
        onOpen={() => onOpenSource(state.version.store, state.version.id)}
      />
      {state.version.summarySource === SummarySource.AI && (
        <div className='section-summary-label'>
          <MdAutoAwesome /> AI Summary
        </div>
      )}
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{state.version.summary}</ReactMarkdown>
      <SectionFooter version={state.version} />
    </>
  );
};

interface SectionFooterProps {
  version: OnboardingSectionVersion;
}

// Names the person who uploaded the source, not the summary's author.
const SectionFooter = ({ version }: SectionFooterProps) => (
  <div className='section-footer'>
    updated {dateToLongDate(version.createdAt)} by {version.createdByName}
  </div>
);

interface SourceBarProps {
  version: OnboardingSectionVersion;
  onOpen: () => void;
}

const SourceBar = ({ version, onOpen }: SourceBarProps) => (
  <div className='section-source-bar'>
    <RiLinkM />
    <Button appearance={Appearance.EMPTY} className='section-source-filename' onClick={onOpen}>
      {version.sourceFileName}
    </Button>
    <span className='section-source-updated'>updated {dateToLongDate(version.createdAt)}</span>
  </div>
);
